import {clamp} from 'lodash-es';
import type {Position} from '@vueuse/core';
import {useIntersectionObserver} from '@vueuse/core';
import {usePinch, useWheel} from '@vueuse/gesture';
import {
	LEAFLET_ZOOM_EXCLUDE_CLASS,
	LEAFLET_ZOOM_SUPPRESS_CLICKS_CLASS,
} from '#shared/constants';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const DOUBLE_TAP_ZOOM = 1.5;
const DOUBLE_TAP_MS = 350;
const DOUBLE_TAP_MOVE_PX = 14;
const VIEWPORT_VISIBILITY_THRESHOLD = 0.3;
const ZOOMED_EPSILON = 0.01;
const DEFAULT_SENSITIVITY = 0.5;
const SENSITIVITY_RANGE = {
	min: 0.0005,
	max: 0.005,
};
const TRANSITION_PROPERTY = 'transform 300ms ease-out';

function isExcludedTarget(target: EventTarget | null | undefined): boolean {
	if (!(target instanceof Element)) {
		return false;
	}

	return Boolean(target.closest(`.${LEAFLET_ZOOM_EXCLUDE_CLASS}`));
}

export function useLeafletZoom(
	contentRef: Ref<HTMLElement | null | undefined>,
	viewportRef: Ref<HTMLElement | null | undefined>,
	enabled: Ref<boolean>,
) {
	const contentSize = useElementSize(contentRef);
	const viewportSize = useElementSize(viewportRef);

	const gesturesEnabled = computed(() => import.meta.client && enabled.value && Boolean(viewportRef.value));

	const zoom = ref(MIN_ZOOM);
	const panRatio = ref<Position>({x: 0, y: 0});
	const isZoomed = computed(() => zoom.value > MIN_ZOOM + ZOOMED_EPSILON);

	const currentOffset = computed<Position>(() => ({
		x: viewportSize.width.value * panRatio.value.x,
		y: viewportSize.height.value * panRatio.value.y,
	}));

	const currentContentSize = computed(() => {
		const baseWidth = isZoomed.value
			? viewportSize.width.value
			: contentSize.width.value;
		const baseHeight = contentSize.height.value;

		return {
			width: baseWidth * zoom.value,
			height: baseHeight * zoom.value,
		};
	});

	const zoomSensitivity = computed(() => {
		const clamped = clamp(DEFAULT_SENSITIVITY, 0, 1);
		return SENSITIVITY_RANGE.min + (clamped * (SENSITIVITY_RANGE.max - SENSITIVITY_RANGE.min));
	});

	const transitionStyleValue = ref<string | undefined>();
	let resetTransitionTimeout: ReturnType<typeof setTimeout> | null = null;
	let accumulatedDelta = 0;
	let rafId: number | null = null;
	let activeGestures = 0;
	let boundViewport: HTMLElement | null = null;
	let lastTapAt = 0;
	let lastTapClientX = 0;
	let lastTapClientY = 0;
	let multiTouchGestureActive = false;
	let panTouchMoved = false;
	let panTouchId: number | null = null;
	let panLastClientX = 0;
	let panLastClientY = 0;

	const pointer = usePointer();

	const pointerPositionRelativeToViewport = computed(() => {
		const viewport = viewportRef.value;

		if (!viewport) {
			return {x: 0, y: 0};
		}

		const rect = viewport.getBoundingClientRect();
		return {
			x: pointer.x.value - rect.left,
			y: pointer.y.value - rect.top,
		};
	});

	function setSuppressChildInteractions(active: boolean) {
		contentRef.value?.classList.toggle(LEAFLET_ZOOM_SUPPRESS_CLICKS_CLASS, active);
	}

	function notifyGestureStart() {
		activeGestures += 1;

		if (activeGestures === 1) {
			setSuppressChildInteractions(true);
		}
	}

	function notifyGestureEnd() {
		activeGestures = Math.max(0, activeGestures - 1);

		if (activeGestures === 0) {
			setSuppressChildInteractions(false);
		}
	}

	function enableTransition(transition: boolean) {
		if (resetTransitionTimeout) {
			clearTimeout(resetTransitionTimeout);
			resetTransitionTimeout = null;
		}

		if (transition) {
			transitionStyleValue.value = TRANSITION_PROPERTY;
			resetTransitionTimeout = setTimeout(() => {
				transitionStyleValue.value = undefined;
				resetTransitionTimeout = null;
			}, 300);
			return;
		}

		transitionStyleValue.value = undefined;
	}

	function clampPanRatio({x, y}: Position): Position {
		if (!viewportRef.value) {
			return {x, y};
		}

		if (viewportSize.width.value <= 0 || viewportSize.height.value <= 0) {
			return {x: 0, y: 0};
		}

		if (!isZoomed.value && contentSize.width.value <= 0) {
			return {x: 0, y: 0};
		}

		const minPanX = (viewportSize.width.value - currentContentSize.value.width) / viewportSize.width.value;
		const minPanY = (viewportSize.height.value - currentContentSize.value.height) / viewportSize.height.value;

		return {
			x: clamp(x, minPanX, 0),
			y: clamp(y, minPanY, 0),
		};
	}

	function computePanRatioFromScale(scale: number): Position {
		const panRatio = -0.5 * (scale - MIN_ZOOM);

		return {
			x: panRatio,
			y: panRatio,
		};
	}

	function updatePanRatio(pos: Position) {
		panRatio.value = clampPanRatio(pos);
	}

	function updateZoom(newZoom: number) {
		zoom.value = clamp(newZoom, MIN_ZOOM, MAX_ZOOM);
	}

	function resetTransformState() {
		zoom.value = MIN_ZOOM;
		panRatio.value = {x: 0, y: 0};
		transitionStyleValue.value = undefined;
	}

	function zoomDelta(deltaY: number) {
		accumulatedDelta += deltaY;

		if (rafId === null) {
			rafId = requestAnimationFrame(processZoom);
		}
	}

	function processZoom() {
		if (!viewportRef.value) {
			return;
		}

		rafId = null;

		if (Math.abs(accumulatedDelta) < 1) {
			accumulatedDelta = 0;
			return;
		}

		const delta = -accumulatedDelta * zoomSensitivity.value;
		const oldZoom = zoom.value;
		updateZoom(zoom.value + delta);

		const actualZoomChange = zoom.value - oldZoom;
		const cursorXRatio = pointerPositionRelativeToViewport.value.x / viewportSize.width.value;
		const cursorYRatio = pointerPositionRelativeToViewport.value.y / viewportSize.height.value;

		if (zoom.value > MIN_ZOOM) {
			updatePanRatio({
				x: panRatio.value.x - cursorXRatio * actualZoomChange,
				y: panRatio.value.y - cursorYRatio * actualZoomChange,
			});
		} else {
			panRatio.value = {x: 0, y: 0};
		}

		accumulatedDelta = 0;
	}

	function zoomToScale(targetScale: number, options?: {animate?: boolean; focal?: Position}) {
		if (options?.animate) {
			enableTransition(true);
		} else {
			enableTransition(false);
		}

		const oldZoom = zoom.value;
		updateZoom(targetScale);
		const actualZoomChange = zoom.value - oldZoom;

		if (zoom.value > MIN_ZOOM + ZOOMED_EPSILON) {
			updatePanRatio(computePanRatioFromScale(zoom.value));
		} else {
			panRatio.value = {x: 0, y: 0};
		}

		if (options?.focal && viewportRef.value && Math.abs(actualZoomChange) > 0) {
			const rect = viewportRef.value.getBoundingClientRect();
			const relativePosition = {
				x: options.focal.x - rect.left,
				y: options.focal.y - rect.top,
			};

			if (zoom.value > MIN_ZOOM) {
				updatePanRatio({
					x: panRatio.value.x - (relativePosition.x / viewportSize.width.value) * actualZoomChange,
					y: panRatio.value.y - (relativePosition.y / viewportSize.height.value) * actualZoomChange,
				});
			}
		}
	}

	function endPanTracking() {
		if (panTouchId === null) {
			return;
		}

		panTouchId = null;
		notifyGestureEnd();
	}

	const wheel = useWheel(
		({event, delta: [_, deltaY], first, last}) => {
			if (!gesturesEnabled.value || isExcludedTarget(event?.target)) {
				return;
			}

			if (first) {
				notifyGestureStart();
			}

			event.preventDefault();
			zoomDelta(deltaY);

			if (last) {
				notifyGestureEnd();
			}
		},
		{
			domTarget: viewportRef,
			eventOptions: {passive: false},
			enabled: gesturesEnabled,
		},
	);

	const pinch = usePinch(
		({delta: [deltaX], event, first, last}) => {
			if (!gesturesEnabled.value || isExcludedTarget(event?.target)) {
				return;
			}

			if (first) {
				endPanTracking();
				notifyGestureStart();
				transitionStyleValue.value = undefined;
			}

			event?.preventDefault();
			zoomDelta(-deltaX * 2);

			if (last) {
				updatePanRatio(panRatio.value);
				multiTouchGestureActive = false;
				notifyGestureEnd();
			}
		},
		{
			domTarget: viewportRef,
			eventOptions: {passive: false},
			enabled: gesturesEnabled,
		},
	);

	function resetGestureInteractionState() {
		activeGestures = 0;
		setSuppressChildInteractions(false);
		multiTouchGestureActive = false;
		panTouchMoved = false;
		panTouchId = null;
	}

	function resetGestures() {
		resetGestureInteractionState();
		wheel.clean();
		pinch.clean();

		if (!gesturesEnabled.value) {
			return;
		}

		wheel.bind();
		pinch.bind();
	}

	const styles = computed(() => {
		if (!gesturesEnabled.value || !isZoomed.value) {
			return undefined;
		}

		return {
			position: 'relative' as const,
			width: '100%',
			transform: `translate(${currentOffset.value.x}px, ${currentOffset.value.y}px) scale(${zoom.value})`,
			transformOrigin: 'top left',
			transition: transitionStyleValue.value,
			willChange: 'transform',
		};
	});

	function reset({animate = false}: {animate?: boolean} = {}) {
		if (animate) {
			enableTransition(true);
		} else {
			enableTransition(false);
		}

		resetTransformState();
		lastTapAt = 0;
		resetGestures();
	}

	function handleDoubleTap(clientX: number, clientY: number) {
		if (isZoomed.value) {
			reset({animate: true});
			return;
		}

		zoomToScale(DOUBLE_TAP_ZOOM, {
			animate: true,
			focal: {x: clientX, y: clientY},
		});
	}

	function registerTap(clientX: number, clientY: number) {
		const now = Date.now();
		const movedFromLastTap = Math.hypot(clientX - lastTapClientX, clientY - lastTapClientY);

		if (now - lastTapAt < DOUBLE_TAP_MS && movedFromLastTap < DOUBLE_TAP_MOVE_PX) {
			handleDoubleTap(clientX, clientY);
			lastTapAt = 0;
			return;
		}

		lastTapAt = now;
		lastTapClientX = clientX;
		lastTapClientY = clientY;
	}

	function onTouchStart(event: TouchEvent) {
		if (!enabled.value) {
			return;
		}

		if (event.touches.length >= 2) {
			multiTouchGestureActive = true;
			endPanTracking();
			return;
		}

		if (!isZoomed.value || event.touches.length !== 1) {
			return;
		}

		const touch = event.touches[0];
		if (!touch || isExcludedTarget(touch.target)) {
			return;
		}

		panTouchId = touch.identifier;
		panLastClientX = touch.clientX;
		panLastClientY = touch.clientY;
		panTouchMoved = false;
		transitionStyleValue.value = undefined;
		notifyGestureStart();
	}

	function onTouchMove(event: TouchEvent) {
		if (panTouchId === null || !isZoomed.value) {
			return;
		}

		const touch = [...event.touches].find(({identifier}) => identifier === panTouchId);
		if (!touch) {
			return;
		}

		event.preventDefault();

		const deltaX = touch.clientX - panLastClientX;
		const deltaY = touch.clientY - panLastClientY;

		if (deltaX !== 0 || deltaY !== 0) {
			panTouchMoved = true;
		}

		panLastClientX = touch.clientX;
		panLastClientY = touch.clientY;

		if (viewportSize.width.value <= 0 || viewportSize.height.value <= 0) {
			return;
		}

		updatePanRatio({
			x: (currentOffset.value.x + deltaX) / viewportSize.width.value,
			y: (currentOffset.value.y + deltaY) / viewportSize.height.value,
		});
	}

	function onTouchEnd(event: TouchEvent) {
		if (!import.meta.client || !enabled.value) {
			return;
		}

		const endedPanTouch = panTouchId !== null
			&& [...event.changedTouches].some(({identifier}) => identifier === panTouchId)
			&& ![...event.touches].some(({identifier}) => identifier === panTouchId);

		if (endedPanTouch) {
			endPanTracking();
		}

		if (event.touches.length > 0) {
			return;
		}

		if (multiTouchGestureActive) {
			multiTouchGestureActive = false;
			return;
		}

		const touch = event.changedTouches[0];
		if (!touch || isExcludedTarget(touch.target)) {
			return;
		}

		if (panTouchMoved) {
			panTouchMoved = false;
			return;
		}

		registerTap(touch.clientX, touch.clientY);
	}

	function bindTapListeners(viewport: HTMLElement) {
		viewport.addEventListener('touchstart', onTouchStart, {passive: true, capture: true});
		viewport.addEventListener('touchmove', onTouchMove, {passive: false, capture: true});
		viewport.addEventListener('touchend', onTouchEnd, {passive: true, capture: true});
		viewport.addEventListener('touchcancel', onTouchEnd, {passive: true, capture: true});
	}

	function unbindTapListeners(viewport: HTMLElement) {
		viewport.removeEventListener('touchstart', onTouchStart, true);
		viewport.removeEventListener('touchmove', onTouchMove, true);
		viewport.removeEventListener('touchend', onTouchEnd, true);
		viewport.removeEventListener('touchcancel', onTouchEnd, true);
	}

	function unbindTapListenersIfNeeded() {
		if (boundViewport) {
			unbindTapListeners(boundViewport);
			boundViewport = null;
		}
	}

	function bindTapListenersIfReady() {
		unbindTapListenersIfNeeded();
		resetGestureInteractionState();

		if (!gesturesEnabled.value) {
			return;
		}

		const viewport = viewportRef.value;
		if (!viewport) {
			return;
		}

		bindTapListeners(viewport);
		boundViewport = viewport;
		resetGestures();
	}

	function teardown() {
		unbindTapListenersIfNeeded();
		endPanTracking();
		wheel.clean();
		pinch.clean();
		resetTransformState();
		lastTapAt = 0;
		resetGestureInteractionState();
	}

	watch(gesturesEnabled, (isEnabled) => {
		if (isEnabled) {
			nextTick(bindTapListenersIfReady);
			return;
		}

		teardown();
	}, {flush: 'post'});

	watch([viewportRef], () => {
		if (gesturesEnabled.value) {
			bindTapListenersIfReady();
		}
	}, {flush: 'post'});

	useIntersectionObserver(
		viewportRef,
		([entry]) => {
			if (!isZoomed.value || !entry) {
				return;
			}

			if (entry.intersectionRatio === 0) {
				reset({animate: false});
			}
		},
		{threshold: [0, VIEWPORT_VISIBILITY_THRESHOLD]},
	);

	onMounted(() => {
		nextTick(bindTapListenersIfReady);
	});

	onBeforeUnmount(teardown);

	return {
		reset,
		isZoomed,
		styles,
	};
}
